import React, { useEffect, useRef, useState } from 'react';
import { TextField, TextFieldProps } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';

import { ApiParams } from '../../../common/api/api';
import { useDebounce } from '../../../common/Debounce.hooks';
import { User } from '../../../user/User.interface';
import { useFindUsers } from '../../../user/User.hooks';

// compare IDs of both arrays
function arrayEquals(a: number[], b?: number[]): boolean {
  return (
    Array.isArray(a) &&
    Array.isArray(b) &&
    a.length === b.length &&
    a.every((val, index) => val === b[index])
  );
}

export interface MultiUserSelectorProps {
  value?: number[];
  disabled?: boolean;
  showEmail?: boolean;
  textFieldProps?: Omit<TextFieldProps, 'variant'>;
  onSelected?: (user: User[]) => void;
  onChange?: (user: User[]) => void;
}

const MultiUserSelector: React.FC<MultiUserSelectorProps> = (
  props: MultiUserSelectorProps
) => {
  const {
    value: userId,
    disabled,
    showEmail,
    textFieldProps,
    onChange,
    onSelected
  } = props;
  const previousUserId = useRef<number[]>(userId || []);
  const [search, setSearch] = useState<string>('');
  const [selected, setSelected] = useState<User[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const debouncedSearch: string = useDebounce(search, 300);
  const { userList, isLoading, error, get } = useFindUsers();

  useEffect(() => {
    const params: ApiParams = {};
    if (debouncedSearch) {
      params.search = debouncedSearch;
    }
    get(params);
  }, [get, debouncedSearch]);

  useEffect(() => {
    userList?.data && setUsers(userList?.data);
  }, [userList]);

  // if userId changes, reselect user
  useEffect(() => {
    if (!arrayEquals(previousUserId.current, userId)) {
      setSelected([]);
      previousUserId.current = userId || [];
    }
  }, [userId]);

  useEffect(() => {
    // if no selected user yet
    if (selected.length > 0) {
      return;
    }
    const selectedUser: User[] = users.filter((user: User) => {
      return userId?.some(uid => uid === user.id);
    });

    if (selectedUser.length > 0) {
      setSelected(selectedUser);
      onSelected?.(selectedUser);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, userId]);

  return (
    <Autocomplete
      multiple
      loading={isLoading}
      disabled={!!disabled}
      value={selected || null}
      options={users}
      getOptionLabel={option => {
        const name = `${option.firstName} ${option.lastName}`;
        return showEmail ? `${name} (${option.email})` : name;
      }}
      getOptionSelected={(option: User) =>
        !!userId?.some((id: number) => id === option.id)
      }
      onInputChange={(_, value: string) => setSearch(value)}
      onChange={(_event: React.ChangeEvent<{}>, value: User[]) => {
        previousUserId.current = value.map(u => u.id);
        setSelected(value);
        onChange?.(value);
      }}
      renderInput={params => (
        <TextField
          {...params}
          fullWidth
          variant='outlined'
          label='Select User'
          placeholder='Choose a User'
          helperText={error?.message}
          {...textFieldProps}
        />
      )}
    />
  );
};

export default MultiUserSelector;
